import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import PaginationFooter from '../../../components/PaginationFooter';
import CatalogFilters from '../catalog/CatalogFilters';
import ItemPickerRow from './ItemPickerRow';
import useCatalogItems, { DEFAULT_CATALOG_FILTERS } from '../../hooks/useCatalogItems';

// Abre nas novidades prontas (revisadas ou publicadas) que ainda não saíram em nenhum envio
const PICKER_FILTERS = {
  ...DEFAULT_CATALOG_FILTERS,
  status: { values: ['reviewed', 'published'], exclude: false },
  sent: 'nao',
};

/**
 * Seletor "Adicionar itens" de um envio: busca e filtros do catálogo, seleção entre páginas.
 * Itens em rascunho, aguardando deploy ou internos ficam desabilitados; já enviados em outro envio só avisam.
 * @param {{ open: boolean, editionId: number, existingIds: Set<number>, onClose: Function, onAdd: Function }} props
 */
function ItemPickerDialog({ open, editionId, existingIds, onClose, onAdd }) {
  const catalog = useCatalogItems({ initialFilters: PICKER_FILTERS, limit: 15, enabled: open });
  const [picked, setPicked] = useState(() => new Map());

  useEffect(() => { if (open) setPicked(new Map()); }, [open]);

  const toggle = (item) => setPicked((prev) => {
    const next = new Map(prev);
    if (next.has(item.id)) next.delete(item.id);
    else next.set(item.id, item);
    return next;
  });

  const handleAdd = () => {
    onAdd(Array.from(picked.values()));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>Adicionar itens</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <CatalogFilters
          filters={catalog.filters}
          onFilterChange={catalog.setFilter}
          searchInput={catalog.searchInput}
          onSearchInputChange={catalog.setSearchInput}
        />
        {catalog.error && <Alert severity="error" sx={{ borderRadius: 0 }}>{catalog.error}</Alert>}
        {catalog.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
        ) : catalog.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
            Nenhuma novidade com esses filtros
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {catalog.items.map((item) => (
              <ItemPickerRow
                key={item.id}
                item={item}
                editionId={editionId}
                alreadyIn={existingIds.has(item.id)}
                checked={picked.has(item.id)}
                onToggle={toggle}
              />
            ))}
          </Box>
        )}
        <PaginationFooter pagination={catalog.pagination} onPageChange={catalog.setPage} itemLabel="novidades" />
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleAdd} disabled={picked.size === 0}>
          {picked.size > 0 ? `Adicionar ${picked.size}` : 'Adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ItemPickerDialog;
